import { z } from 'zod';

// Zod schemas live next to the consumer (auth/) and mirror api/'s contract for /api/auth/*
// and /api/me. Drift is caught by review when api/ contracts change (per project memory:
// hand-written schemas, no codegen).

const deviceSchema = z.object({
  deviceId: z.string().min(1),
  deviceName: z.string().optional(),
  deviceType: z.enum(['web', 'ios', 'android', 'unknown']),
});

export const loginRequestSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
  device: deviceSchema,
});

export const userSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  displayName: z.string(),
  avatarUrl: z.string().nullable().optional(),
  locale: z.enum(['ar', 'en']).optional(),
  isEmailVerified: z.boolean().optional(),
  authMethods: z.array(z.enum(['email', 'google', 'apple', 'guest'])).optional(),
  createdAt: z.string().optional(),
  /**
   * True for an anonymous guest session. Guest "users" are synthesised
   * client-side from a guest doc (no email/displayName) — see `buildGuestUser`
   * in auth-context — so this is never present on a parsed /api/auth/me user.
   */
  isGuest: z.boolean().optional(),
});

export const loginResponseSchema = z.object({
  user: userSchema,
  tokens: z.object({
    accessToken: z.string(),
    // refreshToken is omitted by backend for web clients (sent via httpOnly cookie)
    refreshToken: z.string().optional(),
    accessTokenExpiresAt: z.string(),
    refreshTokenExpiresAt: z.string(),
  }),
  isNewUser: z.boolean().optional(),
});

// api/'s /api/auth/me wraps the user in `{ user: {...} }`.
export const meResponseSchema = z.object({ user: userSchema });

// Guest session — api/'s POST /api/guest/auth/init and /api/guest/auth/refresh.
// A guest doc carries no email/displayName; for web clients the refresh token
// is delivered as an httpOnly cookie and omitted from the body.
export const guestSchema = z.object({
  id: z.string(),
  deviceId: z.string().optional(),
  deviceName: z.string().nullable().optional(),
  deviceType: z.string().optional(),
  status: z.string().optional(),
  createdAt: z.string().optional(),
});

export const guestAuthResponseSchema = z.object({
  accessToken: z.string(),
  accessTokenExpiresAt: z.string(),
  refreshTokenExpiresAt: z.string(),
  refreshToken: z.string().optional(),
  guest: guestSchema,
  isNewGuest: z.boolean().optional(),
});

export type LoginRequest = z.infer<typeof loginRequestSchema>;
export type LoginResponse = z.infer<typeof loginResponseSchema>;
export type User = z.infer<typeof userSchema>;
export type Guest = z.infer<typeof guestSchema>;
export type GuestAuthResponse = z.infer<typeof guestAuthResponseSchema>;
